import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { cubicBezier } from '../utils/easing';

export default function initHeader() {
  const header = document.querySelector('.header');
  if (header) {
    // The header rests at opacity:0 in CSS. Animate it in normally, but under
    // reduced motion snap straight to the visible state so it isn't left hidden.
    const mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', () => {
      gsap.set(header, { y: -30 });
      gsap.to(header, {
        opacity: 1,
        y: 0,
        z: 0,
        duration: 1.6,
        ease: 'power3.out',
        delay: .7,
      });
    });
    mm.add('(prefers-reduced-motion: reduce)', () => {
      gsap.set(header, { opacity: 1, y: 0, z: 0 });
    });

    const hero = document.querySelector('.home__hero');
    if (hero) {
      const updateOverHero = () => {
        if (window.innerWidth <= 991) {
          header.classList.remove('is-over-hero');
          return;
        }
        const heroBottom = hero.getBoundingClientRect().bottom;
        header.classList.toggle('is-over-hero', heroBottom > header.offsetHeight);
      };
      updateOverHero();
      window.addEventListener('scroll', updateOverHero, { passive: true });
      window.addEventListener('resize', updateOverHero);
      ScrollTrigger.addEventListener('refresh', updateOverHero);
    }
  }

  const toggle = document.querySelector('.header__toggle');
  if (!toggle) return;

  const nav = document.querySelector('.header__nav');
  const navSep = document.querySelector('.header__separator--nav');
  const overlay = document.querySelector('.header__overlay');
  const iconGrid = toggle.querySelector('.header__toggle__icon-grid');
  const iconClose = toggle.querySelector('.header__toggle__icon-close');
  const label = toggle.querySelector('span');
  const navLinks = nav.querySelectorAll('.header__nav__list li');

  let isOpen = false;
  let tl = null;

  // Set initial states
  gsap.set(iconClose, { scale: 0.5, rotate: -90 });
  gsap.set(navLinks, { opacity: 0, y: 8, rotateY: -20 });

  // Hover effects
  toggle.addEventListener('mouseenter', () => {
    if (isOpen) {
      gsap.to(toggle, { color: '#000000', duration: 0.25, ease: 'power2.out' });
      gsap.to(iconClose, { rotate: 90, duration: 0.3, ease: 'power3.inOut' });
    } else {
      gsap.to(iconGrid, { rotate: 90, duration: 0.3, ease: 'power3.inOut' });
    }
  });
  toggle.addEventListener('mouseleave', () => {
    if (isOpen) {
      gsap.to(toggle, { color: '#C6C6C6', duration: 0.25, ease: 'power2.out' });
      gsap.to(iconClose, { rotate: 0, duration: 0.3, ease: 'power3.inOut' });
    } else {
      gsap.to(iconGrid, { rotate: 0, duration: 0.3, ease: 'power3.inOut' });
    }
  });

  const ease = cubicBezier(.76, 0, .24, 1);
  const dur = 0.6;
  const isMobile = () => window.innerWidth <= 991;
  let wasMobile = isMobile();

  function resetMenu() {
    if (tl) tl.kill();
    tl = null;
    isOpen = false;
    toggle.setAttribute('aria-expanded', false);
    label.textContent = 'Menu';

    // Clear all GSAP inline styles so CSS takes over cleanly
    gsap.set([nav, navSep, overlay, iconGrid, iconClose, toggle, label, ...navLinks], { clearProps: 'all' });

    // Re-apply initial states
    gsap.set(iconClose, { scale: 0.5, rotate: -90, opacity: 0 });
    gsap.set(navLinks, { opacity: 0, y: 8, rotateY: -20 });
    overlay.style.pointerEvents = 'none';
  }

  window.addEventListener('resize', () => {
    const mobile = isMobile();
    if (mobile !== wasMobile) {
      wasMobile = mobile;
      resetMenu();
    }
  });

  function openMenu() {
    if (tl) tl.kill();
    isOpen = true;
    toggle.setAttribute('aria-expanded', true);

    tl = gsap.timeline({ defaults: { ease, duration: dur } });

    if (isMobile()) {
      overlay.style.pointerEvents = 'auto';
      tl
        .to(overlay, { opacity: 1, duration: 0.4, ease: 'power2.out' }, 0)
        .to(nav, { height: 'auto', opacity: 1 }, 0)
        .to(iconGrid, { opacity: 0, scale: 0.5, rotate: 90, duration: 0.35 }, 0)
        .to(iconClose, { opacity: 1, scale: 1, rotate: 0, duration: 0.35 }, 0)
        .to(toggle, { color: '#C6C6C6', duration: 0.35 }, 0)
        .fromTo(navLinks,
          { opacity: 0, y: 12, rotateY: -40 },
          { opacity: 1, y: 0, rotateY: 0, duration: 0.3, stagger: 0.05, ease: 'power2.out' },
          0.15
        );
    } else {
      tl
        .to(nav, { width: 'auto', opacity: 1 }, 0)
        .to(navSep, { width: 1, marginLeft: '2rem', marginRight: '2rem', opacity: 1 }, 0)
        .to(iconGrid, { opacity: 0, scale: 0.5, rotate: 90, duration: 0.35 }, 0)
        .to(iconClose, { opacity: 1, scale: 1, rotate: 0, duration: 0.35 }, 0)
        .to(toggle, { color: '#C6C6C6', duration: 0.35 }, 0)
        .fromTo(navLinks,
          { opacity: 0, y: 8, rotateY: -40 },
          { opacity: 1, y: 0, rotateY: 0, duration: 0.3, stagger: 0.05, ease: 'power2.out' },
          0.2
        );
    }

    gsap.to(label, {
      opacity: 0, y: -6, duration: 0.15, ease: 'power2.in',
      onComplete: () => {
        label.textContent = 'Close Menu';
        gsap.fromTo(label,
          { opacity: 0, y: 6 },
          { opacity: 1, y: 0, duration: 0.2, ease: 'power2.out' }
        );
      },
    });
  }

  function closeMenu() {
    if (tl) tl.kill();
    isOpen = false;
    toggle.setAttribute('aria-expanded', false);

    tl = gsap.timeline({ defaults: { ease, duration: dur } });

    if (isMobile()) {
      overlay.style.pointerEvents = 'none';
      tl
        .to(overlay, { opacity: 0, duration: 0.35, ease: 'power2.in' }, 0)
        .to(navLinks, { opacity: 0, y: -12, rotateY: -20, duration: 0.2, stagger: 0.03, ease: 'power2.in' }, 0)
        .to(nav, { height: 0, opacity: 0 }, 0.15)
        .to(iconGrid, { opacity: 1, scale: 1, rotate: 0, duration: 0.35 }, 0)
        .to(iconClose, { opacity: 0, scale: 0.5, rotate: -90, duration: 0.35 }, 0)
        .to(toggle, { color: '#000000', duration: 0.35 }, 0);
    } else {
      tl
        .to(navLinks, { opacity: 0, y: -8, rotateY: -20, duration: 0.2, stagger: 0.03, ease: 'power2.in' }, 0)
        .to(navSep, { width: 0, marginLeft: 0, marginRight: 0, opacity: 0, duration: 0.35 }, 0.1)
        .to(nav, { width: 0, opacity: 0 }, 0.15)
        .to(iconGrid, { opacity: 1, scale: 1, rotate: 0, duration: 0.35 }, 0)
        .to(iconClose, { opacity: 0, scale: 0.5, rotate: -90, duration: 0.35 }, 0)
        .to(toggle, { color: '#000000', duration: 0.35 }, 0);
    }

    gsap.to(label, {
      opacity: 0, y: -6, duration: 0.15, ease: 'power2.in',
      onComplete: () => {
        label.textContent = 'Menu';
        gsap.fromTo(label,
          { opacity: 0, y: 6 },
          { opacity: 1, y: 0, duration: 0.2, ease: 'power2.out' }
        );
      },
    });
  }

  toggle.addEventListener('click', () => {
    if (isOpen) closeMenu();
    else openMenu();
  });

  // Close when clicking outside the header
  document.addEventListener('click', (e) => {
    if (isOpen && !header.contains(e.target)) {
      closeMenu();
    }
  });

  // Close when clicking the overlay
  overlay.addEventListener('click', () => {
    if (isOpen) closeMenu();
  });
}
